// The seasonal-event entry on the menu: a small pill at the top ("🎃 Spooky Words · 11 days left")
// and the one-time welcome card it opens. See src/game/season.ts for the window and the language
// gate, and scripts/gen-halloween-pools.mjs for the lesson itself.
//
// Tapping Start is what switches the lesson. Nothing else does — not the launch link, not the
// date — so an event can never move a child off the lesson they were on without them choosing it.
import Phaser from 'phaser';
import i18next from 'i18next';
import { GAME_WIDTH, FONT_STACK } from '@/game/config';
import { addRoundedLabelBg } from '@/game/ui/roundedLabelBg';
import { showPromoCard } from '@/game/ui/promoCard';
import { daysLeft, markEventWelcomed, type EventId } from '@/game/season';

const TILE_Y = 44;

export interface EventTileOptions {
  /** True when the event's lesson is already the active one — the tile then has nothing to offer. */
  alreadyActive: boolean;
  /** Switch to the event's lesson and rebuild the menu. */
  onStart: () => void;
  /** Open the welcome card straight away (store-card launch, or the first launch in the window). */
  welcome: boolean;
}

/** One card at a time — the tile stays tappable while the card is up. */
let cardOpen = false;

export function showEventWelcome(scene: Phaser.Scene, id: EventId, onStart: () => void): void {
  if (cardOpen) return;
  cardOpen = true;
  markEventWelcomed(id);
  showPromoCard(scene, {
    title: i18next.t(`event.${id}.title`),
    lines: i18next.t(`event.${id}.body`).split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean),
    actions: [
      { label: i18next.t(`event.${id}.later`), kind: 'ghost' },
      { label: i18next.t(`event.${id}.start`), kind: 'primary', onClick: onStart },
    ],
  }, {
    closeOnOutsideTap: true,
    onDismiss: () => { cardOpen = false; },
  });
}

export function addEventTile(scene: Phaser.Scene, id: EventId, opts: EventTileOptions): void {
  const left = daysLeft(id, new Date()) || 21;   // a forced preview (`?event=`) has no real window: show a full run
  const label = scene.add.text(GAME_WIDTH / 2, TILE_Y, i18next.t(`event.${id}.tile`, { count: left }), {
    fontFamily: FONT_STACK, fontSize: '22px', color: '#ffe2b8', fontStyle: 'bold',
    padding: { x: 18, y: 8 },
  }).setOrigin(0.5);
  const bg = addRoundedLabelBg(scene, label, { color: 0x3a1d05, alpha: 0.92, radius: 20 });

  // The pill only invites when there is something to start; on the event's own lesson it stays as
  // a quiet banner (still tappable, it just replays the welcome).
  label.setInteractive({ useHandCursor: true });
  label.on('pointerdown', () => showEventWelcome(scene, id, opts.onStart));
  if (!opts.alreadyActive) {
    scene.tweens.add({
      targets: [label, bg], scale: { from: 1, to: 1.05 }, duration: 1200, yoyo: true, repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  if (opts.welcome) scene.time.delayedCall(700, () => showEventWelcome(scene, id, opts.onStart));
}
